import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Chip,
  Grid,
  LinearProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  LocalOffer as OfferIcon,
  Dashboard as DashboardIcon,
  ViewList as ListIcon
} from '@mui/icons-material';
import OfferService from '../../services/offers';
import { CircularProgressCustom, Modal } from '../../components';
import OfferForm from './OfferForm';
import OfferCard from './OfferCard';
import OfferStatsModal from './OfferStatsModal';
import OffersDashboard from './OffersDashboard';

// Visual design tokens (matching Ventas.jsx style)
const PANEL_BG = 'linear-gradient(180deg,#ffffff 0%,#f9fbfd 65%)';
const PANEL_BORDER = '1px solid rgba(0,0,0,0.08)';
const PANEL_RADIUS = 14;
const PANEL_SHADOW = '0 4px 14px rgba(0,0,0,0.06)';
const BG_GRADIENT = 'linear-gradient(120deg,#fafafa 0%,#f5f7fa 35%,#eef3f9 100%)';

const headerBarSx = {
  position: 'sticky',
  top: 0,
  background: 'rgba(255,255,255,0.9)',
  backdropFilter: 'blur(6px)',
  padding: '16px 20px',
  zIndex: 2,
  borderBottom: '1px solid rgba(0,0,0,0.06)',
  display: 'flex',
  flexDirection: { xs: 'column', md: 'row' },
  justifyContent: 'space-between',
  alignItems: { xs: 'flex-start', md: 'center' },
  gap: { xs: 2, md: 0 }
};

const scrollAreaSx = { flex: 1, overflowY: 'auto', padding: '16px 20px' };

const panelBaseSx = {
  display: 'flex',
  flexDirection: 'column',
  background: PANEL_BG,
  border: PANEL_BORDER,
  borderRadius: PANEL_RADIUS,
  boxShadow: PANEL_SHADOW,
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  minHeight: 'calc(100vh - 100px)'
};

const Ofertas = ({ store }) => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [filterActive, setFilterActive] = useState(null); // null = todos, true = activas, false = inactivas
  const [openStatsModal, setOpenStatsModal] = useState(false);
  const [statsOfferId, setStatsOfferId] = useState(null);
  const [statsOfferName, setStatsOfferName] = useState('');
  const [currentTab, setCurrentTab] = useState(0); // 0 = Listado, 1 = Dashboard

  useEffect(() => {
    loadOffers();
  }, []);

  const loadOffers = async () => {
    setLoading(true);
    try {
      const data = await OfferService.getAll();
      setOffers(data || []);
    } catch (err) {
      console.error('Error loading offers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedOffer(null);
    setOpenModal(true);
  };

  const handleEdit = (offer) => {
    setSelectedOffer(offer);
    setOpenModal(true);
  };

  const handleDelete = async (offerId) => {
    if (!window.confirm('¿Seguro que deseas eliminar esta oferta?')) return;
    
    try {
      await OfferService.delete(offerId);
      await loadOffers();
    } catch (err) {
      console.error('Error deleting offer:', err);
      alert('Error al eliminar la oferta');
    }
  };

  const handleToggleActive = async (offerId, active) => {
    try {
      await OfferService.toggleActive(offerId, { active: active });
      await loadOffers();
    } catch (err) {
      console.error('Error toggling offer:', err);
      alert('Error al cambiar estado de la oferta');
    }
  };

  const handleViewStats = (offer) => {
    setStatsOfferId(offer._id);
    setStatsOfferName(offer.name);
    setOpenStatsModal(true);
  };

  const handleSave = async (offerData) => {
    try {
      if (selectedOffer) {
        await OfferService.update(selectedOffer._id, offerData);
      } else {
        await OfferService.create(offerData);
      }
      setOpenModal(false);
      await loadOffers();
    } catch (err) {
      console.error('Error saving offer:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Error al guardar la oferta';
      alert(`Error: ${errorMsg}`);
    }
  };

  const filteredOffers = offers.filter(offer => {
    if (filterActive === null) return true;
    return offer.active === filterActive;
  });

  return (
    <Box
      display='flex'
      minHeight='100%'
      maxWidth='100%'
      flexDirection='column'
      sx={{ background: BG_GRADIENT, p: 2 }}
    >
      {loading && <LinearProgress color='secondary' />}
      
      <Paper elevation={0} sx={panelBaseSx}>
        <Box sx={headerBarSx}>
          <Box display='flex' alignItems='center' gap={2} sx={{ mb: { xs: 2, md: 0 } }}>
            <OfferIcon sx={{ fontSize: 32, color: 'secondary.main' }} />
            <Box>
              <Typography variant='h5' sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                Gestión de Ofertas
              </Typography>
              <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                {filteredOffers.length} ofertas {filterActive === true ? 'activas' : filterActive === false ? 'inactivas' : 'totales'}
              </Typography>
            </Box>
          </Box>
          
          <Box display='flex' gap={2} alignItems='center' sx={{ flexDirection: { xs: 'column', md: 'row' }, width: { xs: '100%', md: 'auto' } }}>
            <Button
              variant='contained'
              color='secondary'
              startIcon={<AddIcon />}
              onClick={handleCreate}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                boxShadow: 3,
                width: { xs: '100%', md: 'auto' }
              }}
            >
              Nueva Oferta
            </Button>
          </Box>
        </Box>

        {/* Tabs de navegación */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs 
            value={currentTab} 
            onChange={(e, newValue) => setCurrentTab(newValue)}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                minHeight: 48
              }
            }}
          >
            <Tab icon={<ListIcon />} iconPosition="start" label="Listado" />
            <Tab icon={<DashboardIcon />} iconPosition="start" label="Dashboard" />
          </Tabs>
        </Box>

        {/* Contenido según la pestaña activa */}
        {currentTab === 0 ? (
          <>
            {/* Filtros solo en vista de listado */}
            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
              <Box display='flex' gap={1} sx={{ flexWrap: 'wrap' }}>
                <Chip 
                  label='Todas' 
                  color={filterActive === null ? 'primary' : 'default'}
                  onClick={() => setFilterActive(null)}
                  size='small'
                />
                <Chip 
                  label='Activas' 
                  color={filterActive === true ? 'success' : 'default'}
                  onClick={() => setFilterActive(true)}
                  size='small'
                />
                <Chip 
                  label='Inactivas' 
                  color={filterActive === false ? 'default' : 'default'}
                  onClick={() => setFilterActive(false)}
                  size='small'
                />
              </Box>
            </Box>

            <Box sx={scrollAreaSx}>
              {loading ? (
                <Box display='flex' justifyContent='center' alignItems='center' minHeight='400px'>
                  <CircularProgressCustom size={80} />
                </Box>
              ) : filteredOffers.length === 0 ? (
                <Box
                  display='flex'
                  flexDirection='column'
                  justifyContent='center'
                  alignItems='center'
                  minHeight='400px'
                  gap={2}
                >
                  <OfferIcon sx={{ fontSize: 80, color: 'text.disabled' }} />
                  <Typography variant='h6' color='text.secondary'>
                    No hay ofertas {filterActive === true ? 'activas' : filterActive === false ? 'inactivas' : 'registradas'}
                  </Typography>
                  <Button
                    variant='outlined'
                    color='secondary'
                    startIcon={<AddIcon />}
                    onClick={handleCreate}
                  >
                    Crear primera oferta
                  </Button>
                </Box>
              ) : (
                <Grid container spacing={3}>
                  {filteredOffers.map((offer) => (
                    <Grid item xs={12} sm={6} lg={4} key={offer._id}>
                      <OfferCard
                        offer={offer}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onToggleActive={() => handleToggleActive(offer._id, !offer.active)}
                        onViewStats={handleViewStats}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          </>
        ) : (
          // Vista de Dashboard
          <Box sx={scrollAreaSx}>
            <OffersDashboard />
          </Box>
        )}
      </Paper>

      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        Content={() => (
          <OfferForm
            offer={selectedOffer}
            store={store}
            onSave={handleSave}
            onCancel={() => setOpenModal(false)}
          />
        )}
      />

      <OfferStatsModal
        open={openStatsModal}
        onClose={() => setOpenStatsModal(false)}
        offerId={statsOfferId}
        offerName={statsOfferName}
      />
    </Box>
  );
};

export default Ofertas;
